import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { AlertDialog } from '../components/ui/AlertDialog'
import { ToastContainer, ToastVariant, ToastProps } from '../components/ui/Toast'

// Types
type AlertVariant = 'success' | 'warning' | 'error' | 'info'
type ConfirmVariant = 'danger' | 'warning' | 'info' | 'success'

interface AlertOptions {
  title: string
  message: string
  variant?: AlertVariant
  buttonText?: string
  onClose?: () => void
}

interface ConfirmOptions {
  title: string
  message: string
  variant?: ConfirmVariant
  confirmText?: string
  cancelText?: string
  icon?: ReactNode
}

interface ToastOptions {
  title: string
  message?: string
  variant?: ToastVariant
  duration?: number
}

interface DialogContextType {
  // Alert methods (require user acknowledgment)
  showAlert: (options: AlertOptions) => void
  showSuccess: (title: string, message: string) => void
  showError: (title: string, message: string) => void
  showWarning: (title: string, message: string) => void
  showInfo: (title: string, message: string) => void

  // Toast methods (auto-dismiss, no user action needed)
  toast: (options: ToastOptions) => void
  toastSuccess: (title: string, message?: string) => void
  toastError: (title: string, message?: string) => void
  toastWarning: (title: string, message?: string) => void
  toastInfo: (title: string, message?: string) => void

  // Confirm method - returns a promise that resolves to true (confirmed) or false (cancelled)
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const DialogContext = createContext<DialogContextType | null>(null)

interface DialogProviderProps {
  children: ReactNode
}

export function DialogProvider({ children }: DialogProviderProps) {
  // Alert state
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertOptions, setAlertOptions] = useState<AlertOptions | null>(null)

  // Confirm state
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions | null>(null)
  const [confirmResolve, setConfirmResolve] = useState<((value: boolean) => void) | null>(null)
  const [confirmLoading, setConfirmLoading] = useState(false)

  // Toast state
  const [toasts, setToasts] = useState<ToastProps[]>([])

  // Toast methods
  const toast = useCallback((options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast: ToastProps = {
      id,
      title: options.title,
      message: options.message,
      variant: options.variant || 'success',
      duration: options.duration || 3000,
      onClose: () => {},
    }
    setToasts((prev) => [...prev, newToast])
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toastSuccess = useCallback((title: string, message?: string) => {
    toast({ title, message, variant: 'success' })
  }, [toast])

  const toastError = useCallback((title: string, message?: string) => {
    toast({ title, message, variant: 'error', duration: 5000 }) // Errors stay longer
  }, [toast])

  const toastWarning = useCallback((title: string, message?: string) => {
    toast({ title, message, variant: 'warning', duration: 4000 })
  }, [toast])

  const toastInfo = useCallback((title: string, message?: string) => {
    toast({ title, message, variant: 'info' })
  }, [toast])

  // Alert methods
  const showAlert = useCallback((options: AlertOptions) => {
    setAlertOptions(options)
    setAlertOpen(true)
  }, [])

  const showSuccess = useCallback((title: string, message: string) => {
    showAlert({ title, message, variant: 'success' })
  }, [showAlert])

  const showError = useCallback((title: string, message: string) => {
    showAlert({ title, message, variant: 'error' })
  }, [showAlert])

  const showWarning = useCallback((title: string, message: string) => {
    showAlert({ title, message, variant: 'warning' })
  }, [showAlert])

  const showInfo = useCallback((title: string, message: string) => {
    showAlert({ title, message, variant: 'info' })
  }, [showAlert])

  const handleAlertClose = () => {
    setAlertOpen(false)
    alertOptions?.onClose?.()
    setAlertOptions(null)
  }

  // Confirm method
  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmOptions(options)
      setConfirmResolve(() => resolve)
      setConfirmOpen(true)
    })
  }, [])

  const handleConfirmClose = () => {
    setConfirmOpen(false)
    setConfirmLoading(false)
    if (confirmResolve) {
      confirmResolve(false)
    }
    setConfirmOptions(null)
    setConfirmResolve(null)
  }

  const handleConfirm = async () => {
    setConfirmLoading(true)
    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 100))
    setConfirmOpen(false)
    setConfirmLoading(false)
    if (confirmResolve) {
      confirmResolve(true)
    }
    setConfirmOptions(null)
    setConfirmResolve(null)
  }

  return (
    <DialogContext.Provider
      value={{
        showAlert,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        toast,
        toastSuccess,
        toastError,
        toastWarning,
        toastInfo,
        confirm,
      }}
    >
      {children}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Alert Dialog */}
      {alertOptions && (
        <AlertDialog
          isOpen={alertOpen}
          onClose={handleAlertClose}
          title={alertOptions.title}
          message={alertOptions.message}
          variant={alertOptions.variant}
          buttonText={alertOptions.buttonText}
        />
      )}

      {/* Confirm Dialog */}
      {confirmOptions && (
        <ConfirmDialog
          isOpen={confirmOpen}
          onClose={handleConfirmClose}
          onConfirm={handleConfirm}
          title={confirmOptions.title}
          message={confirmOptions.message}
          variant={confirmOptions.variant}
          confirmText={confirmOptions.confirmText}
          cancelText={confirmOptions.cancelText}
          icon={confirmOptions.icon}
          isLoading={confirmLoading}
        />
      )}
    </DialogContext.Provider>
  )
}

export function useDialog() {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }
  return context
}
